'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { useUser, useClerk } from '@clerk/nextjs'
import { supabase } from '@/lib/supabase/client'
import toast from 'react-hot-toast'
import { useRouter } from 'next/navigation'
import { useTranslation } from '@/lib/i18n/use-translation'

interface Profile {
  id: string
  email: string
  full_name: string | null
  avatar_url: string | null
  is_admin: boolean
  is_pro: boolean
  subscription_plan: string
  subscription_status: string
  referral_code: string | null
  preferred_language: string
  credits_balance: number
  trial_ends_at: string | null
  status: string
  voice_id: string | null
}

interface AuthContextType {
  user: any | null
  profile: Profile | null
  loading: boolean
  isAdmin: boolean
  signOut: () => Promise<void>
  updateProfile: (data: { full_name?: string; avatar_url?: string }) => Promise<void>
  refreshProfile: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { user, isLoaded: userLoaded } = useUser()
  const { signOut: clerkSignOut } = useClerk()
  const router = useRouter()
  const { t } = useTranslation()

  const [profile, setProfile] = useState<Profile | null>(null)
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    if (!userLoaded) return

    if (user) {
      fetchProfile(user.id)
    } else {
      setProfile(null)
      setIsAdmin(false)
      setLoading(false)
    }
  }, [user, userLoaded])

  const fetchProfile = async (userId: string) => {
    try {
      console.log('🔍 Fetching profile for user:', userId)

      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single()

      if (error) {
        // Se il profilo non esiste, potrebbe essere che il webhook non sia ancora arrivato
        if (error.code === 'PGRST116') {
          console.warn('⚠️ Profile not found, creating fallback profile...')
          // Crea profilo di fallback usando i dati di Clerk
          const fallbackProfile: Profile = {
            id: userId,
            email: user?.primaryEmailAddress?.emailAddress || '',
            full_name: user?.fullName || null,
            avatar_url: user?.imageUrl || null,
            is_admin: false,
            is_pro: false,
            subscription_plan: 'free',
            subscription_status: 'active',
            referral_code: `HITOO${Date.now()}${Math.floor(Math.random() * 1000)}`,
            preferred_language: 'it',
            credits_balance: 100,
            trial_ends_at: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
            status: 'active',
            voice_id: null,
          }

          // Prova a creare il profilo in Supabase
          const { error: insertError } = await supabase
            .from('profiles')
            .insert({
              ...fallbackProfile,
              subscription_status: 'active',
              status: 'active',
              voice_id: null,
              credits_balance: 100,
              trial_ends_at: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(),
            })

          if (insertError) {
            console.error('❌ Error creating fallback profile:', insertError)
          } else {
            console.log('✅ Fallback profile created')
          }

          setProfile(fallbackProfile)
          setIsAdmin(false)
          setLoading(false)
          return
        }

        console.error('❌ Error fetching profile:', error)
        setProfile(null)
        setIsAdmin(false)
        setLoading(false)
        return
      }

      if (data) {
        console.log('✅ Profile loaded:', data.email)
        setProfile(data)
        setIsAdmin(data.is_admin || false)
      }
    } catch (error) {
      console.error('❌ Exception fetching profile:', error)
      setProfile(null)
      setIsAdmin(false)
    } finally {
      setLoading(false)
    }
  }

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id)
    }
  }

  const signOut = async () => {
    try {
      console.log('Starting sign out...')
      setLoading(true)

      console.log('Clearing profile state...')
      setProfile(null)
      setIsAdmin(false)

      console.log('Calling Clerk signOut with redirect...')
      await clerkSignOut({ redirectUrl: '/sign-in' })

      console.log('Sign out successful')
      toast.success(t('signedOut'))
    } catch (error: any) {
      console.error('Sign out error:', error)
      toast.error(t('signOutError'))
    } finally {
      setLoading(false)
    }
  }

  const updateProfile = async (data: { full_name?: string; avatar_url?: string }) => {
    try {
      if (!user) throw new Error('No user logged in')

      const { error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', user.id)

      if (error) throw error

      await fetchProfile(user.id)
      toast.success(t('profileUpdated'))
    } catch (error: any) {
      console.error('Update profile error:', error)
      toast.error(error.message || t('profileUpdateErrorAlt'))
      throw error
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading: loading || !userLoaded,
        isAdmin,
        signOut,
        updateProfile,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

// Default values for SSR/prerendering when context is not available
const defaultAuthContext: AuthContextType = {
  user: null,
  profile: null,
  loading: true,
  isAdmin: false,
  signOut: async () => {},
  updateProfile: async () => {},
  refreshProfile: async () => {},
}

export function useAuth() {
  const context = useContext(AuthContext)
  // During SSR/prerendering, context might be undefined
  // Return default values instead of throwing
  if (context === undefined) {
    // Only throw in development on client-side to help catch bugs
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
      console.warn('useAuth called outside of AuthProvider, using default values')
    }
    return defaultAuthContext
  }
  return context
}
